import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Users,
  Send,
  Upload,
  FileDown,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Building2,
  ArrowRight,
  Bell,
  ClipboardList,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import AIActionPlanner from '../components/AIActionPlanner';
import FirstPulsePanel from '../components/FirstPulsePanel';
import { startDashboardOnboardingTour } from '../lib/onboardingTour';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };
const TEAM_ACCENTS = ['#0D9488', '#6366F1', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6'];

function Gauge({ value = 0, size = 180, label = 'Wellness' }) {
  const r = size === 180 ? 70 : 52;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c - (pct / 100) * c;
  const color = pct >= 70 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
  const dim = size;
  const cx = dim / 2;
  return (
    <div className="relative" style={{ width: dim, height: dim }}>
      <svg width={dim} height={dim} className="-rotate-90">
        <circle cx={cx} cy={cx} r={r} stroke="#E2E8F0" strokeWidth={size === 180 ? 14 : 10} fill="none" />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          stroke={color}
          strokeWidth={size === 180 ? 14 : 10}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-heading font-semibold text-slate-800 ${size === 180 ? 'text-4xl' : 'text-2xl'}`}>
          {size === 180 ? `${Math.round(pct)}%` : Math.round(pct)}
        </span>
        <span className="text-xs text-slate-400 mt-0.5">{label}</span>
      </div>
    </div>
  );
}

function SurveyProgressRing({ completed, total }) {
  const pct = total ? Math.round((completed / total) * 100) : 0;
  const r = 52;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const dim = 140;
  const cx = dim / 2;
  return (
    <div className="relative" style={{ width: dim, height: dim }}>
      <svg width={dim} height={dim} className="-rotate-90">
        <circle cx={cx} cy={cx} r={r} stroke="#E2E8F0" strokeWidth="10" fill="none" />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          stroke="#0D9488"
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl font-semibold text-slate-800">{completed}</span>
        <span className="text-xs text-slate-400 mt-0.5">Completed</span>
      </div>
    </div>
  );
}

function SurveyManagementHub({ completed, pending, total, onReminders, reminding }) {
  const pct = total ? Math.round((completed / total) * 100) : 0;
  return (
    <motion.div
      variants={fadeUp}
      className="glass-card rounded-3xl p-6 md:p-8 mb-8 border border-teal-100 bg-gradient-to-br from-teal-50/50 to-white"
      data-testid="survey-management-hub"
    >
      <div className="flex items-center gap-2 mb-6">
        <ClipboardList className="w-5 h-5 text-teal-600" />
        <h2 className="font-heading text-xl font-semibold text-slate-800">Survey Management Hub</h2>
        <span className="ml-auto text-xs font-semibold px-3 py-1 rounded-full bg-teal-100 text-teal-700">Q1 Active</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
        {/* Circular progress */}
        <div className="flex flex-col items-center">
          <SurveyProgressRing completed={completed} total={total} />
          <p className="text-sm font-semibold text-slate-700 mt-3">Active Q1 Survey Progress</p>
          <p className="text-xs text-slate-500">{completed} of {total} employees responded</p>
        </div>

        {/* Delivery tracker */}
        <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Form Delivery Tracker</p>
          <p className="font-heading text-3xl font-semibold text-slate-800 mb-1">{pending}</p>
          <p className="text-sm text-slate-500">Surveys Remaining</p>
          <p className="text-xs text-amber-600 mt-2 font-medium">Sent but Pending Response</p>
          <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-slate-400 mt-2">{completed} of {total} total employees</p>
        </div>

        {/* Smart reminders */}
        <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm flex flex-col justify-between h-full min-h-[160px] min-w-0">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Smart Reminders</p>
            <p className="text-sm text-slate-500 leading-relaxed">
              Ping employees who haven&apos;t completed the survey. Identities remain fully anonymous to HR.
            </p>
          </div>
          <Button
            onClick={onReminders}
            disabled={reminding || pending === 0}
            className="mt-4 bg-amber-500 hover:bg-amber-600 text-white w-full h-auto min-h-10 py-2.5 px-3 whitespace-normal text-center leading-snug"
            data-testid="dashboard-send-reminders"
          >
            {reminding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <span className="flex flex-col items-center gap-1">
                <span className="inline-flex items-center justify-center gap-2">
                  <Bell className="w-4 h-4 shrink-0" />
                  <span>Send Secure Anonymous Reminders</span>
                </span>
                <span className="text-xs font-medium opacity-90">({pending} pending)</span>
              </span>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function CompanyDashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { show, node } = useToast();
  const [employees, setEmployees] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);
  const tourStarted = useRef(false);

  const isUnlocked = !!(user && user.email);

  useEffect(() => {
    (async () => {
      try {
        const [e, r, a] = await Promise.all([
          axios.get(`${API}/employees`, { withCredentials: true }),
          axios.get(`${API}/reviews`, { withCredentials: true }),
          axios.get(`${API}/analytics/overview`, { withCredentials: true }),
        ]);
        setEmployees(e.data.employees || []);
        setReviews(r.data.reviews || []);
        setAnalytics(a.data);
      } catch {
        /* ignore */
      }
      setLoading(false);
    })();
  }, []);

  // First-time product tour — only on main dashboard when flag is false
  useEffect(() => {
    if (loading || !user || user === false || tourStarted.current) return;
    if (user.has_seen_onboarding_tour) return;

    tourStarted.current = true;
    const timer = window.setTimeout(() => {
      startDashboardOnboardingTour({
        onComplete: async () => {
          if (refreshUser) await refreshUser();
        },
      });
    }, 600);

    return () => window.clearTimeout(timer);
  }, [loading, user, refreshUser]);

  const handleReport = async () => {
    try {
      const resp = await axios.get(`${API}/analytics/report/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Udvega_Executive_Summary.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      show('Executive summary downloaded');
    } catch {
      show('No survey data available yet for a report.', 'error');
    }
  };

  const activeReview = reviews.find((r) => r.status === 'active') || reviews[0];
  const totalEmployees = activeReview?.employee_count ?? employees.length;
  const completedCount =
    activeReview?.completed_count ??
    employees.filter((e) => e.review_status === 'completed').length;
  const pendingCount = Math.max(0, totalEmployees - completedCount);

  const handleReminders = async () => {
    if (!activeReview?.review_id) {
      show('Launch a survey first from the Surveys tab.', 'error');
      return;
    }
    setReminding(true);
    try {
      const { data } = await axios.post(
        `${API}/reviews/${activeReview.review_id}/send-reminders`,
        {},
        { withCredentials: true }
      );
      show(data.message);
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to send reminders', 'error');
    }
    setReminding(false);
  };

  const deptMap = {};
  employees.forEach((e) => {
    const d = e.department || 'Unassigned';
    (deptMap[d] = deptMap[d] || []).push(e);
  });
  const departments = Object.entries(deptMap).map(([name, members]) => ({
    name,
    members,
    count: members.length,
    completed: members.filter((m) => m.review_status === 'completed').length,
    lead: members[0]?.full_name || '—',
  }));

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const hasData = analytics?.has_data;
  const wellness = hasData ? analytics.overall_wellness : 72;
  const activeReviews = reviews.filter((r) => r.status === 'active').length;

  const stats = [
    { label: 'Total Employees', value: analytics?.total_employees ?? employees.length, icon: Users },
    { label: 'Completion Rate', value: `${analytics?.completion_rate ?? 0}%`, icon: CheckCircle2 },
    { label: 'High Risk', value: analytics?.total_high_risk ?? 0, icon: AlertTriangle },
    { label: 'Active Reviews', value: activeReviews, icon: TrendingUp },
  ];

  const quickActions = [
    { label: 'Launch Survey', desc: 'Send a Resilience Review', icon: Send, onClick: () => navigate('/surveys') },
    { label: 'Import Employees', desc: 'Add your workforce', icon: Upload, onClick: () => navigate('/teams') },
    { label: 'Generate Report', desc: 'Download PDF summary', icon: FileDown, onClick: handleReport },
  ];

  const dashboardBody = (
    <motion.div initial="hidden" animate="visible" variants={stagger}>
      <motion.div variants={fadeUp} className="mb-8">
        <h1 className="font-heading text-3xl font-semibold text-slate-800">
          {(user?.company_name || user?.company || 'Company').trim()} Dashboard
        </h1>
      </motion.div>

      {isUnlocked && (
        <SurveyManagementHub
          completed={completedCount}
          pending={pendingCount}
          total={totalEmployees}
          onReminders={handleReminders}
          reminding={reminding}
        />
      )}

      <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {quickActions.map((a) => (
          <button
            key={a.label}
            onClick={a.onClick}
            disabled={!isUnlocked}
            data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, '-')}`}
            className="glass-card rounded-2xl p-5 flex items-center gap-4 text-left hover:shadow-md transition-shadow group disabled:opacity-60"
          >
            <div className="w-11 h-11 rounded-xl gradient-teal flex items-center justify-center shrink-0">
              <a.icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-800">{a.label}</p>
              <p className="text-xs text-slate-500">{a.desc}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 transition-colors" />
          </button>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <motion.div variants={fadeUp} className="glass-card rounded-3xl p-6 flex flex-col items-center justify-center" data-testid="company-pulse">
          <h3 className="font-heading text-lg font-medium text-slate-800 mb-4 self-start">Company Pulse</h3>
          <Gauge value={wellness} />
          <p className="text-xs text-slate-400 mt-4 text-center">
            {hasData ? 'Overall workforce wellness across all reviews' : 'Add employees and launch a pulse survey to see live data'}
          </p>
        </motion.div>

        <motion.div variants={fadeUp} className="lg:col-span-2 grid grid-cols-2 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="glass-card rounded-2xl p-6 flex flex-col justify-between">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-teal-600" />
                </div>
                <span className="text-sm text-slate-500">{s.label}</span>
              </div>
              <p className="font-heading text-3xl font-semibold text-slate-800">{s.value}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {isUnlocked && (
        <motion.div variants={fadeUp} className="mb-8">
          <AIActionPlanner analytics={analytics} enabled={isUnlocked} />
        </motion.div>
      )}

      <motion.div variants={fadeUp}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-medium text-slate-800 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-500" /> Teams &amp; Departments
          </h2>
          {isUnlocked && (
            <Button variant="outline" size="sm" onClick={() => navigate('/teams')} className="border-slate-300">
              Manage Teams
            </Button>
          )}
        </div>

        {departments.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">No teams yet. Import your workforce to get started.</p>
            {isUnlocked && (
              <Button onClick={() => navigate('/teams')} className="bg-teal-600 hover:bg-teal-700 text-white">
                <Upload className="w-4 h-4 mr-2" /> Import Employees
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.map((d, i) => {
              const accent = TEAM_ACCENTS[i % TEAM_ACCENTS.length];
              const pct = d.count ? Math.round((d.completed / d.count) * 100) : 0;
              return (
                <div
                  key={d.name}
                  className="glass-card rounded-2xl p-5"
                  style={{ borderTop: `3px solid ${accent}` }}
                  data-testid={`team-card-${d.name}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-lg font-medium text-slate-800">{d.name}</h3>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${accent}14`, color: accent }}>
                      {d.count} {d.count === 1 ? 'member' : 'members'}
                    </span>
                  </div>
                  <div className="flex -space-x-2 mb-4">
                    {d.members.slice(0, 5).map((m, idx) => (
                      <div
                        key={idx}
                        title={m.full_name}
                        className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-semibold text-white"
                        style={{ background: accent }}
                      >
                        {(m.full_name || '?').charAt(0).toUpperCase()}
                      </div>
                    ))}
                  </div>
                  <div className="text-sm text-slate-500 mb-1">
                    <span className="text-slate-400">Lead:</span> {d.lead}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
                    </div>
                    <span className="text-xs text-slate-400">{d.completed}/{d.count} done</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </motion.div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="company-dashboard">
      {(!user?.subscription_plan || user.subscription_plan === 'free') && (
        <div className="mb-6 rounded-2xl border border-teal-100 bg-teal-50/70 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-600">
            You are on the <span className="font-semibold text-teal-700">Free</span> plan. Upgrade anytime for larger teams and quarterly analytics.
          </p>
          <Button
            size="sm"
            className="bg-teal-600 hover:bg-teal-700 shrink-0"
            onClick={() => navigate('/pricing')}
          >
            Upgrade plan
          </Button>
        </div>
      )}
      <FirstPulsePanel departments={departments} unlocked={isUnlocked} />
      {dashboardBody}
      {node}
    </div>
  );
}
